import Idea from "@/types/Idea";
import Domain, {domainOfProperty} from "@/types/Domain";
import Workflow, {enhanceWorkflow} from "@/types/Workflow";
import workflow from "@/types/Workflow";
import {AccessLevel, allows} from "@/types/AccessLevel";
import {WorkspacePerson} from "@/types/Person";
import {hasAccessRules, hasFieldAccess} from "@/types/Idea";
import {ColoredOption} from "@/types/ColoredOption";
import {nextColor} from "@/utils/colors";
import type {Described} from "@/utils/markdownMentions";

/**
 * Um papel do ambiente — o que a pessoa faz nele. O alias é o que a fórmula cita, e por isso não
 * muda; o nome, sim. A cor é a do chip, como a das opções de domínio.
 */
export type WorkspaceRole = { alias: string, name: string, color?: string }

/**
 * Os papéis como opções coloridas — o chip da tela de acesso, o seletor, a lista do design. O
 * papel sem cor gravada (os de antes da cor) ganha a próxima livre da paleta, na ordem da lista: a
 * mesma regra da linha nova, que nasce com essa cor sugerida.
 */
export function roleOptions(roles: WorkspaceRole[]): Array<ColoredOption & { value: string, color: string }> {
    const used = roles.map(role => role.color).filter((color): color is string => !!color)
    return roles.map((role, index) => {
        const color = role.color || nextColor(used)
        if (!role.color) used.push(color)
        return {id: index, name: role.name, color, value: role.alias}
    })
}

/**
 * Os papéis já com a cor em vigor no próprio papel (`roleOptions`): o que a lista do design mostra, e
 * o que a janela de edição grava — o papel de antes da cor fica com a que já mostrava.
 */
export function coloredRoles(roles: WorkspaceRole[]): WorkspaceRole[] {
    const options = roleOptions(roles)
    return roles.map((role, index) => ({...role, color: options[index].color}))
}

/** A apresentação do ambiente, em markdown, na página de entrada — com as referências (ver `Described`). */
type Workspace = Described & {
    alias: string
    title: string
    /**
     * Encerrado: o ambiente se lê inteiro e não aceita alteração nenhuma.
     *
     * É o sinal que apaga da tela todo botão de alteração. Chega por aqui e não por injeção
     * porque o `workspace` JÁ é prop de toda tela de ambiente, pelo `WorkspaceRouteComponent` —
     * o encanamento que faltava já existia.
     *
     * Quem garante é o servidor, no portão do `systemAuth`: isto aqui é o que a tela mostra, e
     * tela não é garantia.
     */
    closed?: boolean
    /**
     * O nível de acesso de QUEM ESTÁ OLHANDO neste ambiente — não a lista de quem participa, que
     * sai só pela rota de pessoas e só para administrador.
     *
     * Vem pelo mesmo caminho do `closed`, e pelo mesmo motivo: o `workspace` já é prop de toda
     * tela de ambiente. O servidor o acrescenta ao payload de quem pediu (ver o `toPlainFor`).
     *
     * Opcional porque um servidor que ainda não o mandasse deixaria a tela desenhando de menos,
     * que é o lado seguro de errar.
     */
    accessLevel?: AccessLevel
    /**
     * Os papéis do ambiente (Developer, Tester…), que o designer cria e o administrador dá às
     * pessoas, e as fórmulas citam pelo alias (`"tester" in currentUser.roles`). Ausente é nenhum.
     */
    roles?: WorkspaceRole[]
    /** O fuso que diz que dia é "hoje" nas fórmulas; ausente é o padrão (`DEFAULT_TIME_ZONE`). */
    timeZone?: string
    ideas: Idea[]
    workflows: Workflow[]
    domains: Domain[]
    /**
     * As pessoas do ambiente — de onde os atributos de usuário tiram nome, e-mail e foto, e entre as
     * quais quem edita escolhe (`assignable`). Vêm a todo membro, sem o nível e os papéis de cada
     * um, que continuam só na rota de pessoas.
     */
    people: WorkspacePerson[]
}

export function enhanceWorkspace(workspace: Workspace) {
    const enhanceWorkflows = workspace.workflows.map(workflow => enhanceWorkflow(workflow))
    //Ambiente antigo pode voltar sem a lista; o resto do app conta com o array existir
    const domains = workspace.domains || []
    return {
        ...workspace,
        workflows: enhanceWorkflows,
        domains,
        people: workspace.people || [],
        ideaWith(ideaAlias: string) {
            return this.ideas.find(idea => idea.alias === ideaAlias)
        },
        workflowWith(workflowAlias: string) {
            return this.workflows.find(workflow => workflow.alias === workflowAlias)
        },
        domainWith(domainAlias: string | undefined) {
            return domainOfProperty({domain: domainAlias}, this.domains)
        },
        /** O domínio de um atributo de seleção, ou undefined se ele não tiver um. */
        domainOf(property: {domain?: string}) {
            return this.domainWith(property.domain)
        },
        workflowOf(idea: Idea) {
            return this.workflows.find(workflow => workflow.alias === idea.workflow)
        },
        /**
         * Alguma coleção tem fórmula de acesso, do item ou de um campo? Sem nenhuma, a chave-mestra não
         * teria o que mostrar a mais, e não aparece (decisão do usuário). Os campos contam: o servidor
         * os libera também com ela — sem eles, o ambiente só com acesso por campo ficava sem a chave
         * (relato do usuário, no Localidades).
         */
        hasAccessRules(): boolean {
            return this.ideas.some(idea => hasAccessRules(idea) || hasFieldAccess(idea))
        },
        /**
         * O nível de quem olha chega a este degrau da escada?
         *
         * **Não olha o encerramento**, e é por isso que existe separado do `can`: reabrir é
         * escrita num ambiente que não aceita escrita, e o selo da barra só é clicável para
         * quem é `is('admin')`. Fora desse caso, quem decide se um botão aparece é o `can`.
         */
        is(level: AccessLevel): boolean {
            return allows(this.accessLevel, level)
        },
        /**
         * Pode ALTERAR, exigindo este nível? É o que decide se um botão de alteração é
         * DESENHADO.
         *
         * Duas perguntas numa: o nível basta, e o ambiente aceita alteração. Juntas porque todo
         * botão de alteração precisa das duas, e quem perguntasse só uma esqueceria a outra —
         * era o `canWrite` de antes, que só sabia do encerramento.
         *
         * Aqui, e não só no `WorkspaceRouteComponent`, porque quem precisa da resposta não é só
         * tela de rota: o diagrama de classes recebe o ambiente e decide com ela se um clique
         * numa propriedade abre o editor.
         *
         * Isto é o que a TELA mostra. Quem garante é o servidor, no portão do `systemAuth`.
         */
        can(level: AccessLevel): boolean {
            return !this.closed && this.is(level)
        },
        /**
         * A coleção com o fluxo e os domínios PENDURADOS nela — a mesma forma que o servidor
         * devolve para `meta: ["workflow", "domains"]`.
         *
         * Existe para uma tela não ir ao servidor buscar o que já está aqui: o ambiente carregou
         * as coleções, os fluxos e os domínios, e o que falta é só a montagem. É o que deixa o
         * formulário de criar abrir sem requisição nenhuma.
         *
         * **Precisa continuar igual ao que o servidor monta.** É uma forma copiada, e o preço de
         * copiá-la é este: se o `meta` de lá passar a trazer mais coisa, isto aqui precisa saber.
         * Mora num lugar só por isso.
         *
         * Os domínios vão TODOS, e não só os da coleção: quem lê procura pelo alias, e um
         * superconjunto responde igual sem precisar filtrar.
         */
        resolvedIdeaWith(ideaAlias: string) {
            const idea = this.ideaWith(ideaAlias)
            if (!idea) return undefined
            return {...idea, _workflow: this.workflowOf(idea), _domains: this.domains}
        },
        addIdea(idea: Idea) {
            return this.ideas.push(idea)
        },
        removeIdea(idea: Idea) {
            const index = this.ideas.findIndex(current => current.alias === idea.alias)
            return this.ideas.splice(index, 1)
        },
        addDomain(domain: Domain) {
            return this.domains.push(domain)
        },
        removeDomain(domain: Domain) {
            const index = this.domains.findIndex(current => current.alias === domain.alias)
            return this.domains.splice(index, 1)
        },
        addWorkflow(workflow: Workflow) {
            return this.workflows.push(enhanceWorkflow(workflow))
        },
        removeWorkflow(workflow: Workflow) {
            const index = this.workflows.findIndex(current => current.alias === workflow.alias)
            return this.workflows.splice(index, 1)
        }
    }

}

export type EnhancedWorkpace = ReturnType<typeof enhanceWorkspace>
export default Workspace